import firebase from 'firebase/app';
import 'firebase/auth';
import 'firebase/firestore';

import { Plugins } from '@capacitor/core';
const { Storage } = Plugins;

import Google from './Google';
import Model from './Model';
import Field from './Field';
import Fields from './Fields';
import Entries from './Entries';

export default class Form extends Model {
  
    id = null;
    key = null;
    url = null;
    title = '';
    description = '';
    user = '';
    author = '';
    created = '';
    updated = '';
    owner = false;
    writer = false;
    writers = [];
    reader = false;
    readers = [];
    pending = false;
    fields = new Fields();
    entries = new Entries();

    constructor(data = {}) {
      super(data);
      Object.assign(this, data);
      this.fields = new Fields();
      this.entries = new Entries();
    }

    async store() {
      if (this.fields && this.fields && this.fields.length > 0) {
        for (let field of this.fields) {
          field.icon = Field.getIcon(field.type);
        }
      }
      await Storage.set({
        key: `forms/${this.id}`,
        value: JSON.stringify(this)
      });
      return this;
    }

    static async restore(id) {
      const item = await Storage.get({ key: `forms/${id}` });
      let json = JSON.parse(item.value);
      let form = new Form(json);
      let fields = new Fields();
      if (json.fields && json.fields.length > 0) {
        for (let _field of json.fields) {
          let field = new Field(_field);
          field.form = id;
          field.options = _field.options.flat();
          fields.push(field);
        }
      }
      form.fields = fields.sortAsc("ordinal");
      if (json.entries && json.entries.length > 0) {
        let entries = new Entries();
        for (let _entry of json.entries) {
          let entry = new Entry(_entry);
          entry.form = id;
          entries.push(entry);
        }
        form.entries = entries.sortAsc("ordinal");
      }
      else {
        let entries = await Entries.restore(form.id);
        form.entries = entries.sortAsc("ordinal");
      }
      return form;
    }

    static async load(id) {
      let user = firebase.auth().currentUser;
      const ref = firebase.firestore().collection("forms");
      let doc = await ref.doc(id).get();
      let data = doc.data();
      let form = new Form({
        id: id,
        key: data.key,
        url: data.url,
        title: data.title,
        user: data.user,
        author: data.author,
        created: data.created,
        owner: (data.user === user.uid) || (data.owner === user.email),
        writer: data.writers.includes(user.email),
        reader: data.readers.includes(user.email),
        entries: new Entries()
      });
      let fields = new Fields();
      let items = await ref.doc(doc.id).collection("fields").get();
      items.forEach(item => {
        let data = item.data();
        let field = new Field({
          id: item.id,
          form: id,
          title: data.title,
          type: data.type,
          icon: data.icon,
          ordinal: data.ordinal,
          required: data.required,
          options: data.options || []
        });
        fields.push(field);
      });
      form.fields = fields.sortAsc("ordinal");
      return form;
    }

    async invite(email, role="writer") {
      await this.inviteFirestore(email, role);
      await this.inviteSpreadsheet(email, role);
      return this;
    }

    async save() {
      await this.createSpreadsheet();
      await this.createColumns(); 
      await this.createForm();
      await this.createFields();
      await this.updateEntries();
      await this.updateStyling();
      return this;
    }

    async touch() {
      this.updated = new Date().toISOString();
      const firestore = firebase.firestore().collection("forms");
      let document = await firestore.doc(this.id).update({
        updated: this.updated
      });
      return document;
    }

    async updateEntries() {
      for (let entry in this.entries) {
        if (entry.key == undefined || entry.key.length == 0) {
          entry.key = this.key;
          await entry.store();
        }
      }
      return this;
    }

    async inviteFirestore(email, role="writer") {
      const document = firebase.firestore().collection("forms").doc(this.id);
      if (role == "reader") {
        await document.update({
          readers: firebase.firestore.FieldValue.arrayUnion(email)
        });
      }
      else if (role == "writer") {
        await document.update({
          writers: firebase.firestore.FieldValue.arrayUnion(email)
        });
      }
    }

    async inviteSpreadsheet(email, role="writer") {
      let params = {
        type: "user",
        role: role,
        emailAddress: email
      };
      let url = `https://www.googleapis.com/drive/v3/files/${this.key}/permissions`;
      let response = await Google.post(url, params);
      return response;
    }

    async createSpreadsheet() {
      let params = {
        properties: {
          title: this.title
        }
      };
      let url = `https://sheets.googleapis.com/v4/spreadsheets`;
      let response = await Google.post(url, params);
      this.key = response.spreadsheetId;
      this.url = response.spreadsheetUrl;
      return response;
    }

    async createColumns() {
      let headings = ['User', 'Created'];
      for (let field of this.fields) {
        if (field.title && field.title.length > 0) {
          headings.push(field.title);
        }
      }
      let column = String.fromCharCode(96+headings.length).toUpperCase();
      let range = `Sheet1!A1:${column}1`;
      let params = {
        "range": range,
        "majorDimension": "ROWS",
        "values": [headings]
      };
      let url = `https://sheets.googleapis.com/v4/spreadsheets/${this.key}/values/${range}?valueInputOption=USER_ENTERED`;
      let response = await Google.put(url, params);
      return response;  
    }

    async updateStyling() {
      let columns = this.fields.length + 2;
      let params = {
        "requests": [
          {
            "repeatCell": {
              "range": {
                "sheetId": 0,
                "startRowIndex": 0,
                "endRowIndex": 1000
              },
              "cell": {
                "userEnteredFormat": {
                  "wrapStrategy": "CLIP",
                  "verticalAlignment": "TOP",
                  "horizontalAlignment": "LEFT"
                }
              },
              "fields": "userEnteredFormat(wrapStrategy,verticalAlignment,horizontalAlignment)"
            }
          },
          {
            "repeatCell": {
              "range": {
                "sheetId": 0,
                "startRowIndex": 0,
                "endRowIndex": 1
              },
              "cell": {
                "userEnteredFormat": {
                  "backgroundColor": {
                    "red": 0.9,
                    "green": 0.9,
                    "blue": 0.9
                  },
                  "horizontalAlignment": "LEFT",
                  "textFormat": {
                    "foregroundColor": {
                      "red": 0.0,
                      "green": 0.0,
                      "blue": 0.0
                    },
                    "fontSize": 10,
                    "bold": true
                  }
                }
              },
              "fields": "userEnteredFormat(backgroundColor,textFormat,horizontalAlignment)"
            }
          },
          {
            "updateSheetProperties": {
              "properties": {
                "sheetId": 0,
                "gridProperties": {
                  "frozenRowCount": 1
                }
              },
              "fields": "gridProperties.frozenRowCount"
            }
          },
          {
            "updateDimensionProperties": {
              "range": {
                "sheetId": 0,
                "dimension": "COLUMNS",
                "startIndex": 0,
                "endIndex": columns
              },
              "properties": {
                "pixelSize": 220
              },
              "fields": "pixelSize"
            }
          }
        ]
      };
      let url = `https://sheets.googleapis.com/v4/spreadsheets/${this.key}:batchUpdate`;
      let response = await Google.post(url, params);
      return response;
    }

    async createForm() {
      const user = firebase.auth().currentUser;
      const firestore = firebase.firestore().collection("forms");
      let document = await firestore.doc(this.id).set({
        url: this.url,
        key: this.key,
        title: this.title,
        description: this.description,
        user: user.uid,
        email: user.email,
        author: user.displayName,
        created: this.created,
        updated: this.updated,
        owner: user.email,
        readers: [user.email].flat(),
        writers: [user.email].flat()
      });
      return document;
    }

    async createFields() {
      const firestore = firebase.firestore().collection("forms").doc(this.id).collection("fields");
      for (let field of this.fields) {
        await firestore.doc(field.id).set({
          type: field.type,
          title: field.title,
          ordinal: field.ordinal,
          required: field.required,
          options: field.options.flat() || [],
          icon: Field.getIcon(field.type)
        });
      }
      return this;
    }
    
}